// src/pages/languages/LanguageSkillDetails.jsx
import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Volume2,
  PenTool,
  BookOpen,
  Headphones,
  Mic,
  FileText,
  Layers,
  Plus,
  RefreshCw,
  Pencil,
  Trash2,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  languageSkillsAPI,
  getAllSkillItems,
  languageVocabularyAPI,
  languageGrammarAPI,
  languageReadingAPI,
  languageListeningAPI,
  languageSpeakingAPI,
  languageWritingAPI,
} from "../../services/languageService";

const skillTypeConfig = {
  VOCABULARY: { label: "Vocabulary", icon: Volume2, color: "text-blue-600", bg: "bg-blue-50", chip: "bg-blue-100 text-blue-700" },
  GRAMMAR: { label: "Grammar", icon: PenTool, color: "text-purple-600", bg: "bg-purple-50", chip: "bg-purple-100 text-purple-700" },
  READING: { label: "Reading", icon: BookOpen, color: "text-orange-600", bg: "bg-orange-50", chip: "bg-amber-100 text-amber-700" },
  LISTENING: { label: "Listening", icon: Headphones, color: "text-cyan-600", bg: "bg-cyan-50", chip: "bg-cyan-100 text-cyan-700" },
  SPEAKING: { label: "Speaking", icon: Mic, color: "text-pink-600", bg: "bg-pink-50", chip: "bg-orange-100 text-orange-700" },
  WRITING: { label: "Writing", icon: FileText, color: "text-green-600", bg: "bg-green-50", chip: "bg-pink-100 text-pink-700" },
  MIXED: { label: "Mixed", icon: Layers, color: "text-violet-600", bg: "bg-violet-50", chip: "bg-violet-100 text-violet-700" },
};

// GENERAL_PATH هو الاسم القديم لـ MIXED
const isMixedType = (type) => type === "MIXED" || type === "GENERAL_PATH";
const normalizeSkillType = (type) => (type === "GENERAL_PATH" ? "MIXED" : type);

// استخراج id القسم من بيانات المهارة (يدعم أكتر من شكل للـ response)
const getCategoryId = (skill) => {
  const c = skill?.category_id ?? skill?.category;
  return typeof c === "object" ? c?.id : c;
};

// كل الأنواع اللي ممكن تظهر جوه مهارة MIXED (بنفس ترتيب الباك إند)
const CONTENT_TYPES = ["VOCABULARY", "GRAMMAR", "READING", "LISTENING", "SPEAKING", "WRITING"];
const FLAT_MCQ_TYPES = ["VOCABULARY", "GRAMMAR"];
const PARENT_TYPES = ["READING", "LISTENING", "SPEAKING"];

// مهارة MIXED → كل الأنواع، وإلا نوع واحد بس
const typesForSkill = (skillType) =>
  isMixedType(skillType) ? CONTENT_TYPES : [skillType];

// صفحات التعديل (routes) الفعلية في الفرونت. لو نوع معين لسه ملوش صفحة، امسح سطره (الزرار هيبقى ديسيبل).
const editableTypes = {
  VOCABULARY: (skillId, q) => `/dashboard/languages/skills/${skillId}/vocabulary/${q.id}/edit`,
  GRAMMAR: (skillId, q) => `/dashboard/languages/skills/${skillId}/grammar/${q.id}/edit`,
  READING: (skillId, q) => `/dashboard/languages/skills/${skillId}/reading/${q.id}/edit`,
  LISTENING: (skillId, q) => `/dashboard/languages/skills/${skillId}/listening/${q.id}/edit`,
  SPEAKING: (skillId, q) => `/dashboard/languages/skills/${skillId}/speaking/${q.id}/edit`,
  WRITING: (skillId, q) => `/dashboard/languages/skills/${skillId}/writing/${q.id}/edit`,
};

// صفحات الإضافة لكل نوع
const createRoutes = {
  VOCABULARY: (skillId) => `/dashboard/languages/skills/${skillId}/vocabulary/create`,
  GRAMMAR: (skillId) => `/dashboard/languages/skills/${skillId}/grammar/create`,
  READING: (skillId) => `/dashboard/languages/skills/${skillId}/reading/create`,
  LISTENING: (skillId) => `/dashboard/languages/skills/${skillId}/listening/create`,
  SPEAKING: (skillId) => `/dashboard/languages/skills/${skillId}/speaking/create`,
  WRITING: (skillId) => `/dashboard/languages/skills/${skillId}/writing/create`,
};

const addLabels = {
  VOCABULARY: "إضافة سؤال",
  GRAMMAR: "إضافة سؤال",
  READING: "إضافة قطعة",
  LISTENING: "إضافة تسجيل",
  SPEAKING: "إضافة فيديو",
  WRITING: "إضافة سؤال",
};

// حذف العنصر الرئيسي (سؤال مسطّح أو قطعة/تسجيل/فيديو مع كل أسئلته)
const deleteHandlers = {
  VOCABULARY: (id) => languageVocabularyAPI.delete(id),
  GRAMMAR: (id) => languageGrammarAPI.delete(id),
  READING: (id) => languageReadingAPI.deletePassage(id),
  LISTENING: (id) => languageListeningAPI.deleteAudio(id),
  SPEAKING: (id) => languageSpeakingAPI.deleteVideo(id),
  WRITING: (id) => languageWritingAPI.delete(id),
};

// حذف سؤال فرعي جوه قطعة/تسجيل/فيديو.
// اسم الدالة deleteQuestion افتراضي — لو اسمها مختلف في languageService عدّله هنا.
// لو الدالة مش موجودة أصلًا، زرار الحذف للأسئلة الفرعية بيتعطّل تلقائيًا.
const childDeleteApis = {
  READING: languageReadingAPI,
  LISTENING: languageListeningAPI,
  SPEAKING: languageSpeakingAPI,
};
const canDeleteChild = (type) =>
  typeof childDeleteApis[type]?.deleteQuestion === "function";
const deleteChild = (type, id) => childDeleteApis[type].deleteQuestion(id);

const DIFFICULTY_STYLES = {
  EASY: "bg-emerald-50 text-emerald-700",
  MEDIUM: "bg-amber-50 text-amber-700",
  HARD: "bg-rose-50 text-rose-700",
};

const CHOICE_FIELDS = [
  { key: "choice_a", letter: "A" },
  { key: "choice_b", letter: "B" },
  { key: "choice_c", letter: "C" },
  { key: "choice_d", letter: "D" },
];

const PARENT_ICON = { READING: FileText, LISTENING: Headphones, SPEAKING: Mic };

// مفتاح فريد للعنصر: الـ ids بتتكرر بين الأنواع (vocab رقم 5 وgrammar رقم 5)
const entryKey = (type, id) => `${type}-${id}`;

export default function LanguageSkillDetails() {
  const { skillId } = useParams();
  const navigate = useNavigate();

  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);

  const [questions, setQuestions] = useState([]);
  const [qLoading, setQLoading] = useState(true);
  const [qError, setQError] = useState("");
  const restoreKey = `skill-details-restore-${skillId}`;
  const [expandedParents, setExpandedParents] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(restoreKey));
      return new Set(saved?.expanded || []);
    } catch {
      return new Set();
    }
  });

  const [confirmDelete, setConfirmDelete] = useState(null); // { id, type, title, child }
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    fetchSkill();
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillId]);

  useEffect(() => {
    if (qLoading || !skill) return;
    let saved = null;
    try {
      saved = JSON.parse(sessionStorage.getItem(restoreKey));
    } catch {}
    if (!saved) return;
    sessionStorage.removeItem(restoreKey);

    console.log("[restore]", saved);
    const id = saved.childId
      ? `entry-${saved.key}-child-${saved.childId}`
      : `entry-${saved.key}`;
    // بنحاول كذا مرة عشان لو الـ layout أو الصور غيّروا السكرول
    [0, 150, 400, 800].forEach((ms) =>
      setTimeout(() => {
        const el = document.getElementById(id);
        if (!el) console.warn("[restore] element not found:", id);
        el?.scrollIntoView({ block: "center" });
      }, ms)
    );
  }, [qLoading, skill]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSkill = async () => {
    try {
      setLoading(true);
      const data = await languageSkillsAPI.getOne(skillId);
      setSkill(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      setQLoading(true);
      setQError("");
      setQuestions(await getAllSkillItems(skillId));
    } catch (e) {
      console.error(e);
      setQError("فشل تحميل الأسئلة");
    } finally {
      setQLoading(false);
    }
  };

  // تجميع العناصر حسب النوع (للـ MIXED كل نوع في قسمه)
  const entriesByType = useMemo(() => {
    const map = {};
    for (const type of CONTENT_TYPES) map[type] = [];
    for (const entry of questions) {
      const type = entry.type || normalizeSkillType(skill?.skill_type);
      if (map[type]) map[type].push(entry);
    }
    return map;
  }, [questions, skill]);

  // إجمالي الأسئلة الفعلي (الأسئلة الفرعية بتتحسب مش القطع)
  const totalQuestions = useMemo(
    () =>
      questions.reduce(
        (sum, entry) =>
          sum + (PARENT_TYPES.includes(entry.type) ? (entry.questions || []).length : 1),
        0
      ),
    [questions]
  );

  const toggleParent = (key) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const goEdit = (type, item, childId = null) => {
    const path = editableTypes[type]?.(skillId, item);
    if (!path) return;
    try {
      sessionStorage.setItem(
        restoreKey,
        JSON.stringify({
          key: entryKey(type, item.id),
          childId,
          expanded: [...expandedParents],
        })
      );
    } catch {}
    navigate(path, { state: { question: item } });
  };

  const askDelete = (type, id, title, child = false) => {
    setDeleteError("");
    setConfirmDelete({ id, type, title: title || "هذا العنصر", child });
  };

  const handleCancelDelete = () => {
    if (deleting) return;
    setConfirmDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    const { id, type, child } = confirmDelete;

    try {
      setDeleting(true);
      setDeleteError("");
      if (child) {
        if (!canDeleteChild(type)) {
          setDeleteError("حذف الأسئلة الفرعية غير متاح لهذا النوع");
          return;
        }
        await deleteChild(type, id);
      } else {
        const handler = deleteHandlers[type];
        if (!handler) {
          setDeleteError("نوع السؤال غير مدعوم للحذف");
          return;
        }
        await handler(id);
      }
      setConfirmDelete(null);
      await fetchQuestions();
      fetchSkill();
    } catch (e) {
      console.error(e);
      setDeleteError("فشل حذف العنصر، حاول مرة أخرى");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!skill) return null;

  const normalizedType = normalizeSkillType(skill.skill_type);
  const config = skillTypeConfig[normalizedType] || {};
  const Icon = config.icon || BookOpen;
  const availableTypes = typesForSkill(skill.skill_type);

  // الرجوع لصفحة مهارات القسم: /dashboard/languages/categories/:categoryId/skills
  const categoryId = getCategoryId(skill);
  const goBackToSkills = () => {
    if (categoryId) {
      navigate(`/dashboard/languages/categories/${categoryId}/skills`);
    } else {
      navigate(-1); // احتياطي لو القسم مش موجود في الـ response
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={goBackToSkills}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          <ArrowRight className="w-4 h-4" />
          رجوع للمهارات
        </button>

        <div className="card flex items-center gap-4">
          <div className={`${config.bg} rounded-2xl p-4`}>
            <Icon className={`w-8 h-8 ${config.color}`} />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">{skill.title}</h1>
            <p className={`text-sm font-medium ${config.color}`}>{config.label}</p>
            {skill.description && (
              <p className="text-gray-500 text-sm mt-1">{skill.description}</p>
            )}
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {qLoading ? skill.total_questions ?? 0 : totalQuestions}
            </p>
            <p className="text-xs text-gray-500">إجمالي الأسئلة</p>
          </div>
        </div>
      </div>

      {/* شريط التحديث + الأخطاء */}
      <div className="flex items-center gap-2">
        <h2 className="font-bold text-gray-900">أسئلة المهارة</h2>
        <button
          onClick={fetchQuestions}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
          title="تحديث"
        >
          <RefreshCw className={`w-4 h-4 ${qLoading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {qError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{qError}</span>
          <button onClick={fetchQuestions} className="font-medium underline">
            Retry
          </button>
        </div>
      )}

      {/* قسم لكل نوع محتوى متاح جوه المهارة */}
      {!qError &&
        availableTypes.map((type) => (
          <ContentTypeSection
            key={type}
            type={type}
            skillId={skillId}
            collapsible={isMixedType(skill.skill_type)}
            loading={qLoading}
            items={entriesByType[type] || []}
            expandedParents={expandedParents}
            onToggleParent={toggleParent}
            onEdit={goEdit}
            onDelete={(id, title) => askDelete(type, id, title)}
            onDeleteChild={(id, title) => askDelete(type, id, title, true)}
            onAdd={() => navigate(createRoutes[type](skillId))}
          />
        ))}

      {/* ── مودال تأكيد الحذف ── */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-right">
            <h3 className="font-bold text-gray-900 mb-2">تأكيد الحذف</h3>
            <p className="text-sm text-gray-500 mb-2">
              هل أنت متأكد من حذف "{confirmDelete.title}"؟
              {!confirmDelete.child && PARENT_TYPES.includes(confirmDelete.type) && (
                <span className="block mt-1 text-red-500">
                  سيتم حذف كل الأسئلة الفرعية المرتبطة به أيضًا.
                </span>
              )}
            </p>
            {deleteError && (
              <p className="text-xs text-red-600 mb-2">{deleteError}</p>
            )}
            <div className="flex gap-2 justify-end mt-4">
              <button
                onClick={handleCancelDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                إلغاء
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleting}
                className="px-4 py-2 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? "جاري الحذف..." : "حذف"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// قسم نوع واحد (Vocabulary / Grammar / Reading / Listening / Speaking / Writing)
// ============================================
function ContentTypeSection({
  type,
  skillId,
  loading,
  items,
  expandedParents,
  onToggleParent,
  onEdit,
  onDelete,
  onDeleteChild,
  onAdd,
  collapsible = false,
}) {
  // القسم مفتوح افتراضيًا، والسهم (في المختلط بس) بيقفله ويفتحه
  const [collapsed, setCollapsed] = useState(false);
  const typeInfo = skillTypeConfig[type] || {
    label: type,
    chip: "bg-gray-100 text-gray-700",
  };
  const isParentType = PARENT_TYPES.includes(type);
  const isMcqType = FLAT_MCQ_TYPES.includes(type);
  const canEdit = Boolean(editableTypes[type]);
  const canDelete = Boolean(deleteHandlers[type]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        {collapsible ? (
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            title={collapsed ? "إظهار الأسئلة" : "إخفاء الأسئلة"}
            className="flex items-center gap-2 flex-1 text-right"
          >
            {collapsed ? (
              <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
            )}
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeInfo.chip}`}
            >
              {typeInfo.label}
            </span>
            <span className="text-sm text-gray-400">{items.length} عنصر</span>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <span
              className={`text-xs font-semibold px-2.5 py-1 rounded-full ${typeInfo.chip}`}
            >
              {typeInfo.label}
            </span>
            <span className="text-sm text-gray-400">{items.length} عنصر</span>
          </div>
        )}
        {createRoutes[type] && (
          <button
            onClick={onAdd}
            className="flex items-center gap-1.5 text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            {addLabels[type]}
          </button>
        )}
      </div>

      {!collapsed && (loading ? (
        <div className="px-5 py-6 text-center text-sm text-gray-400">جاري التحميل...</div>
      ) : items.length === 0 ? (
        <div className="px-5 py-6 text-center text-sm text-gray-400">
          لا يوجد محتوى بعد
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {items.map((item) => {
            const key = entryKey(type, item.id);

            if (isParentType) {
              return (
                <ParentRow
                  key={key}
                  rowKey={key}
                  type={type}
                  item={item}
                  expanded={expandedParents.has(key)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onToggle={() => onToggleParent(key)}
                  onEditParent={(childId) => onEdit(type, item, childId)}
                  onDeleteParent={() => onDelete(item.id, item.title)}
                  onDeleteChild={(q) => onDeleteChild(q.id, q.question_text)}
                />
              );
            }

            if (isMcqType) {
              return (
                <McqRow
                  key={key}
                  domId={`entry-${key}`}
                  item={item}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onEdit={() => onEdit(type, item)}
                  onDelete={() => onDelete(item.id, item.question_text)}
                />
              );
            }

            return (
              <FlatRow
                key={key}
                domId={`entry-${key}`}
                item={item}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={() => onEdit(type, item)}
                onDelete={() => onDelete(item.id, item.title)}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

// أزرار تعديل/حذف مشتركة
function RowActions({ canEdit, canDelete, onEdit, onDelete, editTitle, deleteTitle }) {
  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      <button
        onClick={() => canEdit && onEdit()}
        disabled={!canEdit}
        title={editTitle || (canEdit ? "تعديل" : "التعديل غير متاح لهذا النوع بعد")}
        className={`p-1.5 rounded-lg transition-colors ${
          canEdit
            ? "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
            : "text-gray-300 cursor-not-allowed"
        }`}
      >
        <Pencil className="w-4 h-4" />
      </button>
      <button
        onClick={() => canDelete && onDelete()}
        disabled={!canDelete}
        title={deleteTitle || (canDelete ? "حذف" : "الحذف غير متاح لهذا النوع")}
        className={`p-1.5 rounded-lg transition-colors ${
          canDelete
            ? "text-gray-400 hover:text-red-600 hover:bg-red-50"
            : "text-gray-300 cursor-not-allowed"
        }`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

// صف "مسطّح" بسيط (Writing — عنوان بس)
function FlatRow({ item, canEdit, canDelete, onEdit, onDelete, domId }) {
  return (
    <div id={domId} className="flex items-start justify-between gap-3 px-5 py-4">
      <div className="min-w-0 flex-1 text-right">
        <p className="text-sm font-medium text-gray-900 truncate">
          {item.title || item.question_text}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {item.difficulty} · {item.points} نقطة
          {item.min_words ? ` · ${item.min_words}-${item.max_words} كلمة` : ""}
        </p>
      </div>
      <RowActions
        canEdit={canEdit}
        canDelete={canDelete}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
}

// كارت كامل لسؤال MCQ (Vocabulary / Grammar / الأسئلة التابعة لـ Reading-Listening-Speaking)
function McqRow({
  item,
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
  editTitle,
  deleteTitle,
  compact = false,
  domId,
}) {
  const choices = CHOICE_FIELDS.map(({ key, letter }) => ({
    letter,
    text: item[key],
  })).filter((c) => c.text);

  return (
    <div id={domId} dir="ltr" className={compact ? "text-left" : "px-5 py-4 text-left"}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-medium text-gray-900 leading-relaxed whitespace-pre-wrap">
            {item.question_text}
          </p>
          {item.translation && (
            <p className="sabr-translation mt-1 text-sm text-gray-600 whitespace-pre-wrap" dir="auto">
              {item.translation}
            </p>
          )}
          {item.question_image && (
            <img
              src={item.question_image}
              alt=""
              className="mt-2 max-h-32 rounded-lg border border-gray-100 object-contain"
            />
          )}
        </div>
        <RowActions
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={onEdit}
          onDelete={onDelete}
          editTitle={editTitle}
          deleteTitle={deleteTitle}
        />
      </div>

      {choices.length > 0 && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {choices.map((c) => {
            const isCorrect = c.letter === item.correct_answer;
            return (
              <div
                key={c.letter}
                className={`flex items-center gap-2 text-xs px-3 py-2 rounded-lg border text-left ${
                  isCorrect
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800 font-medium"
                    : "bg-gray-50 border-gray-100 text-gray-600"
                }`}
              >
                <span
                  className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold flex-shrink-0 ${
                    isCorrect
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-200 text-gray-500"
                  }`}
                >
                  {c.letter}
                </span>
                <span className="min-w-0 flex-1 break-words">{c.text}</span>
                {isCorrect && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
              </div>
            );
          })}
        </div>
      )}

      {(item.explanation || item.english_explanation) && (
        <div className="mt-2.5 space-y-2">
          {item.explanation && (
            <div className="text-xs text-gray-500 text-left leading-relaxed" dir="rtl">
              <span className="font-medium text-gray-600">الشرح: </span>
              {item.explanation}
            </div>
          )}
          {item.english_explanation && (
            <div className="text-xs text-gray-500 text-left leading-relaxed">
              <span className="font-medium text-gray-600">Explanation: </span>
              {item.english_explanation}
            </div>
          )}
        </div>
      )}

      <div className="mt-2.5 flex items-center justify-start gap-2 text-xs">
        {item.difficulty && (
          <span
            className={`px-2 py-0.5 rounded-full font-medium ${
              DIFFICULTY_STYLES[item.difficulty] || "bg-gray-100 text-gray-600"
            }`}
          >
            {item.difficulty}
          </span>
        )}
        {typeof item.points === "number" && (
          <span className="text-gray-400">{item.points} نقطة</span>
        )}
      </div>
    </div>
  );
}

// صف "أب" (قطعة قراءة / تسجيل صوتي / فيديو) مع الأسئلة التابعة له، قابل للطي
function ParentRow({
  type,
  rowKey,
  item,
  expanded,
  canEdit,
  canDelete,
  onToggle,
  onEditParent,
  onDeleteParent,
  onDeleteChild,
}) {
  const Icon = PARENT_ICON[type] || FileText;
  const children = item.questions || [];
  const mediaUrl =
    type === "LISTENING" ? item.audio_file : type === "SPEAKING" ? item.video_file : null;
  const childDeletable = canDeleteChild(type);

  return (
    <div id={`entry-${rowKey}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <button
          onClick={onToggle}
          className="flex items-center gap-2 min-w-0 flex-1 text-right"
        >
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-900 truncate">
            {item.title}
          </span>
          <span className="text-xs text-gray-400 flex-shrink-0">
            ({children.length} سؤال)
          </span>
          {item.difficulty && (
            <span
              className={`text-[11px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                DIFFICULTY_STYLES[item.difficulty] || "bg-gray-100 text-gray-600"
              }`}
            >
              {item.difficulty}
            </span>
          )}
        </button>
        <RowActions
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={() => onEditParent()}
          onDelete={onDeleteParent}
          deleteTitle="حذف كامل العنصر مع كل أسئلته الفرعية"
        />
      </div>

      {/* ─── نص القراءة ─── */}
      {(item.passage_image || item.audio_image) && (
        <div className="px-8 pb-3">
          <img
            src={item.passage_image || item.audio_image}
            alt=""
            className="max-h-48 rounded-xl border border-gray-100 object-contain"
          />
        </div>
      )}

      {type === "READING" && item.passage_text && (
        <div className="px-8 pb-4">
          <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 leading-relaxed whitespace-pre-wrap" dir="ltr">
            {item.passage_text}
          </div>
          {item.translation && (
            <p className="sabr-translation mt-1 text-sm text-gray-600 whitespace-pre-wrap" dir="auto">
              {item.translation}
            </p>
          )}
        </div>
      )}

      {/* ─── ميديا اللستنينج (صوت + نص مكتوب) ─── */}
      {type === "LISTENING" && (mediaUrl || item.transcript) && (
        <div className="px-8 pb-4 space-y-3">
          {mediaUrl && (
            <audio controls src={mediaUrl} className="w-full h-10" preload="none" />
          )}
          {item.transcript && (
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 leading-relaxed whitespace-pre-wrap" dir="ltr">
              <p className="font-medium text-gray-700 mb-1 text-xs" dir="rtl">النص المكتوب</p>
              {item.transcript}
            </div>
          )}
        </div>
      )}

      {/* ─── ميديا السبيكنج (فيديو + وصف) ─── */}
      {type === "SPEAKING" && (mediaUrl || item.description) && (
        <div className="px-8 pb-4 space-y-3">
          {mediaUrl && (
            <video
              controls
              src={mediaUrl}
              poster={item.video_image || undefined}
              className="w-full max-h-72 rounded-xl bg-black"
              preload="none"
            />
          )}
          {item.description && (
            <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 leading-relaxed whitespace-pre-wrap" dir="ltr">
              {item.description}
            </div>
          )}
        </div>
      )}

      {expanded && (
        <div className="bg-gray-50 divide-y divide-gray-100 border-t border-gray-100">
          {children.length === 0 ? (
            <div className="px-8 py-3 text-xs text-gray-400">لا يوجد أسئلة بعد</div>
          ) : (
            children.map((q) => (
              <div key={q.id} id={`entry-${rowKey}-child-${q.id}`} className="px-8 py-3">
                <McqRow
                  item={q}
                  compact
                  // تعديل الأسئلة الفرعية بيتم من صفحة تعديل العنصر الأب
                  canEdit={canEdit}
                  editTitle="تعديل الأسئلة من صفحة تعديل العنصر"
                  onEdit={() => onEditParent(q.id)}
                  canDelete={childDeletable}
                  deleteTitle={childDeletable ? "حذف السؤال" : "حذف الأسئلة الفرعية غير متاح"}
                  onDelete={() => onDeleteChild(q)}
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}